<template>
  <div class="subscriptions-v3">
    <v-alert v-if="loadError" type="error">{{ loadError }}</v-alert>

    <v-card class="mb-4">
      <v-card-title class="br">Search subscriptions</v-card-title>
      <v-card-text>
        <v-form ref="searchForm" @submit.prevent="search">
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filters.license_sn"
                label="License s/n"
                maxlength="33"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <date-input
                v-model="filters.date"
                label="Subscription covers date"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.active"
                :items="activeOptions"
                label="Active today"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3" class="d-flex align-center">
              <v-btn
                color="primary"
                type="submit"
                :loading="loading"
                class="mr-2"
                >Search</v-btn
              >
              <v-btn text :disabled="loading" @click="resetSearch">Reset</v-btn>
            </v-col>
          </v-row>
        </v-form>
        <div>
          Active means today is within the subscription's start and end dates,
          inclusive (UTC). The date filter searches for periods covering the
          selected day.
        </div>
      </v-card-text>
    </v-card>

    <v-data-table
      :headers="headers"
      :items="subscriptions"
      :loading="loading"
      :options.sync="options"
      :server-items-length="total"
      :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
      class="elevation-1"
      no-data-text="No subscriptions found."
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title><h2 class="br">Subscriptions</h2></v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            :disabled="loading"
            aria-label="Refresh subscriptions"
            @click="initialize"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-toolbar>
      </template>
      <template v-slot:item.starts_at="{ item }">{{
        formatDate(item.starts_at)
      }}</template>
      <template v-slot:item.ends_at="{ item }">{{
        formatDate(item.ends_at)
      }}</template>
      <template v-slot:item.bandwidth="{ item }">{{
        formatBandwidth(item.bandwidth)
      }}</template>
      <template v-slot:item.br_release_cutoff="{ item }">
        {{
          item.br_release_cutoff
            ? formatDate(item.br_release_cutoff)
            : "All releases"
        }}
      </template>
      <template v-slot:item.active="{ item }">
        <v-chip
          small
          dark
          :color="item.active ? 'green darken-2' : 'red darken-2'"
        >
          {{ item.active ? "Yes" : "No" }}
        </v-chip>
      </template>
      <template v-slot:item.payment_type="{ item }">{{
        item.payment_type || "—"
      }}</template>
      <template v-slot:item.payment_reference="{ item }">{{
        item.payment_reference || "—"
      }}</template>
    </v-data-table>
  </div>
</template>

<script>
import DateInput from "../components/DateInput.vue";
import { http_client } from "../services/api.js";
import { formatDate, parseDate } from "../utils/dates.js";

const emptyFilters = () => ({ license_sn: "", date: "", active: null });
const KBIT_PER_GBIT = 1000000;

export default {
  components: { DateInput },
  data: () => ({
    filters: emptyFilters(),
    appliedFilters: {},
    subscriptions: [],
    total: 0,
    options: {
      page: 1,
      itemsPerPage: 25,
      sortBy: ["starts_at"],
      sortDesc: [true]
    },
    activeOptions: [
      { text: "All", value: null },
      { text: "Active", value: true },
      { text: "Inactive", value: false }
    ],
    headers: [
      { text: "License s/n", value: "license_sn" },
      { text: "Starts", value: "starts_at" },
      { text: "Ends", value: "ends_at" },
      { text: "Bandwidth", value: "bandwidth" },
      { text: "Release cutoff", value: "br_release_cutoff" },
      { text: "Active", value: "active" },
      { text: "Payment type", value: "payment_type" },
      { text: "Payment reference", value: "payment_reference" }
    ],
    loading: false,
    loadError: "",
    requestNumber: 0
  }),
  watch: {
    options: {
      deep: true,
      handler() {
        this.initialize();
      }
    }
  },
  created() {
    this.initialize();
  },
  beforeDestroy() {
    this.requestNumber++;
  },
  methods: {
    async initialize() {
      const request = ++this.requestNumber;
      this.loading = true;
      this.loadError = "";
      try {
        const response = await http_client.get("/subscriptions_v3", {
          params: {
            ...this.appliedFilters,
            page: this.options.page,
            per_page: this.options.itemsPerPage,
            sort_by: this.options.sortBy[0] || "starts_at",
            descending: this.options.sortBy.length
              ? !!this.options.sortDesc[0]
              : true
          }
        });
        if (request !== this.requestNumber) return;
        this.subscriptions = response.data.items;
        this.total = response.data.total;
        // Records may have changed since the previous request.
        const lastPage = Math.max(
          1,
          Math.ceil(this.total / this.options.itemsPerPage)
        );
        if (this.options.page > lastPage) this.options.page = lastPage;
      } catch (error) {
        if (request !== this.requestNumber) return;
        this.subscriptions = [];
        this.total = 0;
        this.loadError = this.errorMessage(
          error,
          "Unable to load subscriptions. Please try again."
        );
      } finally {
        if (request === this.requestNumber) this.loading = false;
      }
    },
    search() {
      if (!this.$refs.searchForm.validate()) return;
      const params = {};
      Object.entries(this.filters).forEach(([key, value]) => {
        const normalized = typeof value === "string" ? value.trim() : value;
        if (
          normalized !== null &&
          normalized !== "" &&
          normalized !== undefined
        ) {
          params[key] = key === "date" ? parseDate(normalized) : normalized;
        }
      });
      this.appliedFilters = params;
      if (this.options.page !== 1) this.options.page = 1;
      else this.initialize();
    },
    resetSearch() {
      this.filters = emptyFilters();
      this.appliedFilters = {};
      this.$nextTick(() => this.$refs.searchForm.resetValidation());
      if (this.options.page !== 1) this.options.page = 1;
      else this.initialize();
    },
    formatDate,
    formatBandwidth(value) {
      if (value >= KBIT_PER_GBIT) return value / KBIT_PER_GBIT + " Gbit/s";
      if (value >= 1000) return value / 1000 + " Mbit/s";
      return value + " kbit/s";
    },
    errorMessage(error, fallback) {
      const detail =
        error.response && error.response.data && error.response.data.detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail)) return detail.map(item => item.msg).join("; ");
      return fallback;
    }
  }
};
</script>
